// useVoiceInput — Web Speech API voice-to-text input for the chat composer.
//
// Extracted verbatim from `AIChatBot.js` (the in-component speech-recognition
// effect + `toggleListening` callback) as part of the behavior-preserving
// refactor (issue #240). Split into its own focused hook (per the task's
// allowance for a small additional hook) so `useChatSession` stays centered on
// the send/receive/history flow.
//
// Owns the `isListening` state and the `recognitionRef`. The setup effect is
// preserved exactly:
//   - no-op when the browser lacks `SpeechRecognition`/`webkitSpeechRecognition`
//     (returns undefined → no recognizer, the mic button is hidden by the UI),
//   - configures `continuous = false`, `interimResults = false`, `lang = 'en-US'`,
//   - `onresult` sets the transcript via the injected `setInputMessage` and
//     clears `isListening`,
//   - `onerror` / `onend` clear `isListening`,
//   - the cleanup calls `recognition.stop()` inside a try/catch that swallows
//     errors (identical to the source).
//
// `toggleListening` preserves the source's branch order and dependency
// (`[isListening]`): no-op without a recognizer, otherwise stop when listening
// or start + set listening when idle.
//
// `use`-prefixed, contains no JSX and performs no rendering.
//
// Validates: Requirements 3.2, 3.4, 1.4

import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Wires the Web Speech API for voice input, pushing recognized transcripts into
 * the chat composer via the injected setter.
 *
 * @param {(value: string) => void} setInputMessage - Setter for the composer
 *   text; called with the recognized transcript (a stable `useState` setter from
 *   the caller, matching the source where it was in-component).
 * @returns {{ isListening: boolean, toggleListening: () => void }} The listening
 *   flag and the start/stop toggle.
 */
export function useVoiceInput(setInputMessage) {
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);

  // Voice input (Web Speech API)
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInputMessage(transcript);
      setIsListening(false);
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognitionRef.current = recognition;

    return () => {
      try { recognition.stop(); } catch (e) { /* ignore */ }
    };
  }, [setInputMessage]);

  const toggleListening = useCallback(() => {
    if (!recognitionRef.current) return;
    if (isListening) {
      recognitionRef.current.stop();
    } else {
      recognitionRef.current.start();
      setIsListening(true);
    }
  }, [isListening]);

  return { isListening, toggleListening };
}

export default useVoiceInput;
